/**
 * @file: useDashboardTaskListMetrics.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import {
  kDashboardHeaderHeight,
  kDashboardToolbarHeight,
  kDashboardToolbarMarginTop,
  kDashboardVerticalPadding,
} from '@euler/app/tv/functions/constants';
import { useScreenContentPadding } from '@euler/app/tv/functions/utils';
import { computeGridLayoutItemSize } from '@euler/functions/layoutMetrics';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useMemo } from 'react';
import { useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const kCellMargin = 40;
const kCellWidth = 500;

export type TaskListMode =
  | 'horizontal-complete'
  | 'vertical-complete'
  | 'vertical-compact';

export type TaskPack<T> = {
  tasks: T[];
  capacity: number;
  rowIndex: number;
  colIndex: number;
};

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const useDashboardTaskListMetrics = <T extends unknown>({
  preferredMode,
  maxTaskPerPack,
  tasks,
}: {
  preferredMode: TaskListMode;
  tasks: T[];
  maxTaskPerPack?: number;
}) => {
  const { rpx } = useResponsiveContext();
  const { width, height } = useWindowDimensions();
  const [paddingLeft] = useScreenContentPadding();
  const insets = useSafeAreaInsets();
  const cellMargin = rpx(kCellMargin);

  const containerHeight =
    height -
    insets.top -
    insets.bottom -
    rpx(kDashboardHeaderHeight) -
    rpx(kDashboardToolbarHeight) -
    rpx(kDashboardToolbarMarginTop) -
    rpx(kDashboardToolbarMarginTop) -
    rpx(kDashboardVerticalPadding) * 2;

  const columns = Math.floor(width / (rpx(kCellWidth) + cellMargin));
  let numberOfColumns =
    preferredMode === 'horizontal-complete'
      ? columns >= 4
        ? columns + 0.25
        : 3.25
      : columns;

  let [cellWidth, cellHeight] = computeGridLayoutItemSize({
    numberOfColumns,
    gridWidth: width - paddingLeft,
    cellMargin: { left: 0, top: 0, bottom: 0, right: cellMargin },
    aspectRatio: 548 / (preferredMode === 'vertical-compact' ? 106 : 312),
    gridPadding: 0,
  });

  const maxPackCapacity = Math.floor(containerHeight / cellHeight);

  const result = useMemo(() => {
    let mode: TaskListMode = preferredMode;
    const count = tasks.length;

    const partition = (numberOfTasksPerPack: number): TaskPack<T>[] => {
      const res: TaskPack<T>[] = [];
      for (let i = 0; i < count; i += numberOfTasksPerPack) {
        res.push({
          capacity: numberOfTasksPerPack,
          tasks: tasks.slice(i, i + numberOfTasksPerPack),
          rowIndex:
            numberOfTasksPerPack > 1 ? 0 : Math.floor(count / numberOfColumns),
          colIndex: numberOfTasksPerPack > 1 ? i : i % numberOfColumns,
        });
      }
      return res;
    };

    // split the tasks to packets
    let packs = partition(
      preferredMode === 'horizontal-complete'
        ? maxTaskPerPack ?? maxPackCapacity
        : 1,
    );

    if (
      preferredMode === 'horizontal-complete' &&
      packs.length < Math.ceil(numberOfColumns) &&
      count > 0 &&
      (maxTaskPerPack == null || maxTaskPerPack > 1)
    ) {
      // turn the horizontal complete list to vertical complete list.
      packs = partition(1);
      mode = 'vertical-complete';
    }

    return { packs, mode };
  }, [preferredMode, tasks, maxTaskPerPack, maxPackCapacity, numberOfColumns]);

  // update task list metrics if the target mode is automatically switched.
  if (result.mode !== 'horizontal-complete' && numberOfColumns % 1 !== 0) {
    numberOfColumns = Math.floor(numberOfColumns);
    [cellWidth, cellHeight] = computeGridLayoutItemSize({
      numberOfColumns,
      gridWidth: width - paddingLeft,
      cellMargin: { left: 0, top: 0, bottom: 0, right: cellMargin },
      aspectRatio: 548 / (result.mode === 'vertical-compact' ? 106 : 312),
      gridPadding: 0,
    });
  }

  return {
    cellWidth,
    cellHeight,
    cellMargin,
    maxPackCapacity,
    numberOfColumns,
    ...result,
  };
};
